import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getFirestore, type Firestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage'
import {
  initializeAuth,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { getMessaging, isSupported as isMessagingSupported, type Messaging } from 'firebase/messaging'
import { firebaseConfig } from '../../config/firebase'

export class FirebaseProvider {
  private static instance: FirebaseProvider
  private app: FirebaseApp
  private db: Firestore
  private storage: FirebaseStorage
  private auth: Auth
  private functions: Functions
  private messaging: Messaging | null = null
  private static isTestEnvironment: boolean = false

  private constructor() {
    
    if (!getApps().length) {
      this.app = initializeApp({
        ...firebaseConfig
      })
    } else {
      this.app = getApps()[0]
    }

    this.db = getFirestore(this.app)
    this.storage = getStorage(this.app)
    // initializeAuth y no getAuth(): getAuth() arranca con IndexedDB como
    // almacén primario, encuentra la sesión en localStorage (donde la deja
    // Firebase al final de cada arranque) y la MIGRA: la copia a IndexedDB y
    // la BORRA de localStorage. Ese borrado es compartido por todas las
    // pestañas, así que otra pestaña arrancando en esa ventana no encuentra
    // sesión en ningún lado y emite un usuario nulo: era lo que mandaba al
    // login al abrir varias pestañas de golpe (KILA-370). Con localStorage
    // como primario la sesión se lee donde está y nunca se mueve. IndexedDB
    // queda de respaldo solo para rescatar una sesión que hubiera quedado
    // ahí a medio migrar.
    this.auth = initializeAuth(this.app, {
      persistence: [browserLocalPersistence, indexedDBLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    })
    this.functions = getFunctions(this.app)

    // Connect to emulators in test environment
    if (process.env.NODE_ENV === 'test') {
      connectFirestoreEmulator(this.db, 'localhost', 8080)
      connectStorageEmulator(this.storage, 'localhost', 9199)
      connectAuthEmulator(this.auth, 'http://localhost:9099')
    }

    // Connect to Functions emulator in development
    if (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
      connectFunctionsEmulator(this.functions, '127.0.0.1', 5001)
    }
  }

  public static getInstance(): FirebaseProvider {
    if (!FirebaseProvider.instance) {
      FirebaseProvider.instance = new FirebaseProvider()
    }
    return FirebaseProvider.instance
  }

  /**
   * Firebase Cloud Messaging para las notificaciones push del correo. Se crea
   * bajo demanda: en navegadores sin soporte (o en pruebas) devuelve null y el
   * dashboard sigue mostrando los avisos por el listener de Firestore.
   */
  public async getMessaging(): Promise<Messaging | null> {
    if (this.messaging) return this.messaging
    if (typeof window === 'undefined' || !(await isMessagingSupported().catch(() => false))) return null
    this.messaging = getMessaging(this.app)
    return this.messaging
  }

  public getApp(): FirebaseApp {
    return this.app
  }

  public getFirestore(): Firestore {
    return this.db
  }

  public getStorage(): FirebaseStorage {
    return this.storage
  }

  public getAuth(): Auth {
    return this.auth
  }

  public getApp(): FirebaseApp {
    return this.app
  }

  public getFunctions(): Functions {
    return this.functions
  }

  // Método para testing
  public static setTestEnvironment(isTest: boolean) {
    this.isTestEnvironment = isTest
    if (this.instance) {
      this.instance = new FirebaseProvider()
    }
  }

  public static isTest(): boolean {
    return this.isTestEnvironment
  }

  // Método para resetear la instancia (útil en testing)
  public static resetInstance() {
    this.instance = undefined
    this.isTestEnvironment = false
  }
}